import { createContext, useContext, useEffect, useRef, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './lib/supabase'
import { createEditorHandoff } from './lib/api'
import { translate, useI18n } from './lib/i18n'
import { Home } from './pages/Home'
import { LegalPage, legalRouteFromPath } from './pages/Legal'
import { DemoPage } from './pages/Demo'
import { EmbedPromptPage } from './pages/EmbedPrompt'
import { EmbedAuthPage } from './pages/EmbedAuth'
import { BootScreen } from './components/BootScreen'
import { AccountRestore } from './pages/AccountRestore'
import { deletionPurgeAt } from './lib/accountDeletionNotice'
import { TransferAccept } from './pages/TransferAccept'
import { ProjectHandoverWizard } from './pages/ProjectHandoverWizard'
import { TeamInviteAccept } from './pages/TeamInviteAccept'
import { consumeTransferToken, handoverIdFromSearch, rememberTransferToken, transferIdFromSearch, transferTokenFromPath } from './lib/transferInvite'
import { consumeTeamToken, rememberTeamToken, teamTokenFromPath } from './lib/teamInvite'
import { Login, PasswordReset, SignUp } from './pages/Login'
import { ToastProvider } from './components/Toast'
import { completeAuthAttempt } from './lib/lastAuthProvider'
import { applyTheme, persistThemePreference, useResolvedTheme, type VelticaTheme } from '@webbuilder/ui'
import { signOutEverywhere } from './lib/editorSession'

type Theme = VelticaTheme

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
}

export const ThemeContext = createContext<ThemeContextType>({
  theme: 'light',
  toggleTheme: () => {},
})

export function useTheme() {
  return useContext(ThemeContext)
}

function transferAccount(session: Session) {
  const email = session.user.email ?? ''
  const meta = session.user.user_metadata as { full_name?: string; name?: string; user_name?: string; avatar_url?: string; picture?: string } | undefined
  const userName = (meta?.full_name || meta?.name || meta?.user_name || (email ? email.split('@')[0] : '') || translate('common.user')).trim()
  return {
    userName,
    displayName: userName.length > 20 ? `${userName.slice(0, 20).trimEnd()}…` : userName,
    avatarUrl: meta?.avatar_url || meta?.picture || '',
  }
}

function allowedRedirectTo(): string | null {
  const raw = new URLSearchParams(window.location.search).get('redirectTo')
  if (!raw) return null

  try {
    const url = new URL(raw)
    const isEditor = url.hostname === 'editor.veltica.com'
    const isLocalEditor = (url.hostname === 'localhost' || url.hostname === '127.0.0.1') && url.port === '5173'
    const hasAllowedProtocol = url.protocol === 'https:' || isLocalEditor
    return (hasAllowedProtocol && (isEditor || isLocalEditor)) ? url.toString() : null
  } catch {
    return null
  }
}

function isPasswordRecoveryUrl(): boolean {
  if (window.location.pathname === '/reset-password') return true

  const search = new URLSearchParams(window.location.search)
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  return search.get('type') === 'recovery' || hash.get('type') === 'recovery'
}

export default function App() {
  const { t } = useI18n()
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [passwordRecovery, setPasswordRecovery] = useState(isPasswordRecoveryUrl)
  const [handoffError, setHandoffError] = useState('')
  const [handoffAttempt, setHandoffAttempt] = useState(0)
  const handoffStartedRef = useRef(false)
  const redirectTo = allowedRedirectTo()
  /* `/legal` es el índice y cada documento tiene su propia dirección: Google
     pide una para la política de privacidad al verificar la app. */
  const legalRoute = legalRouteFromPath(window.location.pathname)
  const isLegalPage = legalRoute !== null
  /* La demo pública de veltica.com: se usa sin cuenta, y con cuenta también
     (lleva su diseño al panel en vez de a registrarse). Ver `pages/Demo`. */
  const isDemoPage = window.location.pathname === '/demo'
  /* La caja de IA que se incrusta en veltica.com. Ver `pages/EmbedPrompt`. */
  const isEmbedPrompt = window.location.pathname === '/embed/prompt'
  /* Y la ventana de acceso que abre esa caja al enviar. Ver `pages/EmbedAuth`. */
  const isEmbedAuth = window.location.pathname === '/embed/auth'
  /**
   * La invitación a recibir un sitio, si se llegó por su enlace.
   *
   * Se lee UNA vez, al montar, y desde ese momento manda el estado. Leerla de
   * la dirección en cada render la perdería en cuanto algo reescribiera la ruta
   * —que es justo lo que hace el efecto de abajo cuando no hay sesión—.
   */
  const [inviteToken, setInviteToken] = useState<string | null>(() => transferTokenFromPath())
  /* La invitación al equipo, en paralelo y no compartiendo estado con la de
     traspaso: las dos pueden estar vivas a la vez —una agencia que te entrega un
     sitio y a la vez te mete en otro— y con una sola variable la segunda
     pisaría a la primera. */
  const [teamInviteToken, setTeamInviteToken] = useState<string | null>(() => teamTokenFromPath())
  /* El traspaso abierto desde el panel, que llega por `?transfer=` en vez de por
     el enlace del correo. Se lee al montar por lo mismo que los otros dos, y va
     aparte porque no comparte nada con ellos salvo la pantalla de destino. */
  const [reviewTransferId, setReviewTransferId] = useState<string | null>(() => transferIdFromSearch())
  /* El repaso posterior a aceptar. Tiene dirección propia porque su paso de
     cobros sale a Stripe y vuelve por una redirección: sin ella el asistente se
     perdería justo en el único paso que no se puede hacer aquí dentro. */
  const [handoverProjectId, setHandoverProjectId] = useState<string | null>(() => handoverIdFromSearch())
  
  /* Lo que se pinta, ya resuelto si la preferencia es «automático». La
     preferencia se elige en el menú de la cuenta; aquí sólo se escucha. */
  const theme = useResolvedTheme()

  useEffect(() => {
    applyTheme(theme, { light: '#f5f5f5', dark: '#181818' })
  }, [theme])

  /* El botón suelto de las pantallas de entrada: pasa al contrario de lo que se
     ve y deja de ser automático, que es lo que se espera al pulsarlo. */
  const toggleTheme = () => {
    persistThemePreference(theme === 'light' ? 'dark' : 'light')
  }

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setLoading(false)
    }).catch(() => setLoading(false))
    const { data: listener } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true)
      if (event === 'SIGNED_IN' && s) completeAuthAttempt(s.user)
      setSession(s)
    })
    return () => listener.subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (loading) return
    if (passwordRecovery) {
      if (window.location.pathname !== '/reset-password') {
        window.history.replaceState(null, '', '/reset-password')
      }
      return
    }
    if (!session) {
      /* La invitación sobrevive al desvío.
         Quien recibe un sitio casi nunca tiene cuenta —por eso se invita a un
         correo y no a un id—, así que el camino normal pasa por registrarse. Sin
         guardarla aquí, la ruta se reescribe a `/login` y el token se pierde:
         quien acababa de crear su cuenta aterrizaba en un panel vacío sin una
         sola pista de que venía a recibir algo. */
      if (inviteToken) rememberTransferToken(inviteToken)
      if (teamInviteToken) rememberTeamToken(teamInviteToken)
      const isPublicPage = window.location.pathname === '/login' || window.location.pathname === '/signup' || isLegalPage || isDemoPage || isEmbedPrompt || isEmbedAuth
      if (!isPublicPage) {
        const url = new URL(window.location.href)
        url.pathname = '/login'
        window.history.replaceState(null, '', url.toString())
      }
      return
    }

    /* Con sesión ya hecha: se recoge lo que quedó guardado antes de entrar y se
       consume, para que rechazar y seguir navegando no vuelva a traer aquí en
       cada recarga. */
    if (!inviteToken) {
      const pending = consumeTransferToken()
      if (pending) {
        setInviteToken(pending)
        window.history.replaceState(null, '', `/transfer/${pending}`)
        return
      }
    }
    if (!teamInviteToken) {
      const pendingTeam = consumeTeamToken()
      if (pendingTeam) {
        setTeamInviteToken(pendingTeam)
        window.history.replaceState(null, '', `/equipo/${pendingTeam}`)
        return
      }
    }
    if (redirectTo) return
    if (window.location.pathname === '/login' || window.location.pathname === '/signup') {
      window.history.replaceState(null, '', '/')
    }
  }, [inviteToken, teamInviteToken, isLegalPage, isDemoPage, isEmbedPrompt, isEmbedAuth, loading, passwordRecovery, redirectTo, session])

  useEffect(() => {
    if (loading || passwordRecovery || !session || !redirectTo || handoffStartedRef.current) return
    /* Una cuenta en camino de borrarse no pasa al editor: primero, restaurar. */
    if (deletionPurgeAt(session)) return
    handoffStartedRef.current = true
    setHandoffError('')

    void createEditorHandoff(session.access_token)
      .then(({ tokenHash }) => {
        const destination = new URL(redirectTo)
        const hash = new URLSearchParams(destination.hash.replace(/^#/, ''))
        hash.set('veltica_handoff', tokenHash)
        destination.hash = hash.toString()
        window.location.replace(destination.toString())
      })
      .catch(() => {
        handoffStartedRef.current = false
        setHandoffError(t('app.handoffFailed'))
      })
  }, [handoffAttempt, loading, passwordRecovery, redirectTo, session, t])

  function finishPasswordRecovery() {
    setPasswordRecovery(false)
    window.history.replaceState(null, '', session ? '/' : '/login')
  }

  /* La caja incrustada en otra página no enseña la pantalla de arranque del
     panel: es un bloque de esa página, y mientras se sabe si hay sesión se
     pinta ya (la sesión sólo decide a dónde lleva al enviar). */
  if (loading && isEmbedAuth) return null
  if (loading && !(isEmbedPrompt && !new URLSearchParams(window.location.search).get('start'))) return <BootScreen theme={theme} />

  if (session && redirectTo && !deletionPurgeAt(session)) {
    return (
      <BootScreen theme={theme}>
        {handoffError ? (
          <div style={{ display: 'grid', justifyItems: 'center', gap: 12, color: theme === 'dark' ? '#EDEDED' : '#374151' }}>
            <span>{handoffError}</span>
            <button
              type="button"
              onClick={() => setHandoffAttempt((value) => value + 1)}
              style={{ border: 0, borderRadius: 8, padding: '8px 14px', background: theme === 'dark' ? '#F0F0F0' : '#111827', color: theme === 'dark' ? '#111' : '#fff', cursor: 'pointer' }}
            >
              {t('app.retry')}
            </button>
          </div>
        ) : undefined}
      </BootScreen>
    )
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {isEmbedAuth
        ? <EmbedAuthPage hasSession={Boolean(session) && !deletionPurgeAt(session)} theme={theme} onToggleTheme={toggleTheme} />
        : isEmbedPrompt
        ? <EmbedPromptPage hasSession={Boolean(session) && !deletionPurgeAt(session)} />
        : isDemoPage
        ? <DemoPage hasSession={Boolean(session) && !deletionPurgeAt(session)} />
        : legalRoute
        ? <LegalPage route={legalRoute} />
        : passwordRecovery
        ? <PasswordReset hasSession={Boolean(session)} onComplete={finishPasswordRecovery} />
        : !session
          ? window.location.pathname === '/signup'
            ? <SignUp theme={theme} onToggleTheme={toggleTheme} />
            : <Login theme={theme} onToggleTheme={toggleTheme} />
        : deletionPurgeAt(session)
          /* La cuenta está en su ventana de gracia: antes que nada —invitación,
             traspaso o panel—, decidir si se restaura. Ver `AccountRestore`. */
          ? <AccountRestore session={session} purgeAt={deletionPurgeAt(session)!} theme={theme} onToggleTheme={toggleTheme} />
        : inviteToken
          /* Antes que el panel: quien llega por este enlace viene a una sola
             cosa, y muchas veces acaba de crear la cuenta y no tiene nada suyo
             que enmarcar todavía. */
          ? <ToastProvider>
              <TransferAccept
                token={session.access_token}
                inviteToken={inviteToken}
                account={transferAccount(session)}
                onChangeAccount={async () => {
                  rememberTransferToken(inviteToken)
                  await signOutEverywhere({ scope: 'local' })
                }}
                onDone={() => {
                  setInviteToken(null)
                  /* Recarga entera y no un cambio de estado: al aceptar, la
                     lista de proyectos del panel y su caché en `localStorage`
                     acaban de quedarse viejas — les falta el sitio que se
                     acaba de recibir. */
                  window.location.replace('/')
                }}
              />
            </ToastProvider>
        : handoverProjectId
          /* Antes que el panel, como la propia invitación: quien acaba de
             recibir un sitio viene a terminar de recibirlo, y el taller de fondo
             con sus cifras compite con lo único que hay que hacer ahora. */
          ? <ToastProvider>
              <ProjectHandoverWizard
                token={session.access_token}
                projectId={handoverProjectId}
                onDone={() => {
                  setHandoverProjectId(null)
                  /* Al proyecto, con la dirección limpia: recargar el repaso al
                     volver atrás enseñaría un asistente ya resuelto. */
                  window.location.replace(`/?project=${handoverProjectId}`)
                }}
              />
            </ToastProvider>
        : reviewTransferId
          /* El mismo destino que el enlace del correo, por el otro camino: aquí
             ya hay sesión y probablemente proyectos, pero la decisión es la
             misma y el taller de fondo competiría con ella. */
          ? <ToastProvider>
              <TransferAccept
                token={session.access_token}
                transferId={reviewTransferId}
                account={transferAccount(session)}
                onDone={() => {
                  setReviewTransferId(null)
                  window.location.replace('/')
                }}
              />
            </ToastProvider>
        : teamInviteToken
          ? <ToastProvider>
              <TeamInviteAccept
                token={session.access_token}
                inviteToken={teamInviteToken}
                onChangeAccount={async () => {
                  rememberTeamToken(teamInviteToken)
                  await signOutEverywhere({ scope: 'local' })
                }}
                onDone={() => {
                  setTeamInviteToken(null)
                  /* Recarga entera, como en el traspaso: al aceptar, la lista de
                     proyectos y su caché en `localStorage` acaban de quedarse
                     viejas — les falta el sitio al que se acaba de entrar. */
                  window.location.replace('/')
                }}
              />
            </ToastProvider>
          : <ToastProvider><Home session={session} /></ToastProvider>}
    </ThemeContext.Provider>
  )
}
